import * as _ from "lodash";
import * as moment from "moment";
import * as M from "../models";
import { Utility } from ".";

const sentenceCase = require("sentence-case");

/**
 * Clones an instance of a class
 * @param inst Instance to clone
 */
export const cloneInstance = <T>(inst: any) => {
    return Object.assign(Object.create(Object.getPrototypeOf(inst)), inst) as T;
};

/**
 * Converts html to plain text.
 * @param html
 */
export const decodeHtml = (html: string | undefined | null) => {
    if (html === undefined || html === null)
        return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
};

export const delay = (time: number) => {
    return new Promise((resolve) => setTimeout(() => resolve(), time));
};

export const diacriticsRemovalMap: Array<{ base: string; letters: string }> = [
    { base: "A", letters: "\u0041\u24B6\uFF21\u00C0\u00C1\u00C2\u1EA6\u1EA4\u1EAA\u1EA8\u00C3\u0100\u0102\u1EB0\u1EAE\u1EB4\u1EB2\u0226\u01E0\u00C4\u01DE\u1EA2"
                        + "\u00C5\u01FA\u01CD\u0200\u0202\u1EA0\u1EAC\u1EB6\u1E00\u0104\u023A\u2C6F" },
    { base: "AA", letters: "\uA732" },
    { base: "AE", letters: "\u00C6\u01FC\u01E2" },
    { base: "AO", letters: "\uA734" },
    { base: "AU", letters: "\uA736" },
    { base: "AV", letters: "\uA738\uA73A" },
    { base: "AY", letters: "\uA73C" },
    { base: "B", letters: "\u0042\u24B7\uFF22\u1E02\u1E04\u1E06\u0243\u0182\u0181" },
    { base: "C", letters: "\u0043\u24B8\uFF23\u0106\u0108\u010A\u010C\u00C7\u1E08\u0187\u023B\uA73E" },
    { base: "D", letters: "\u0044\u24B9\uFF24\u1E0A\u010E\u1E0C\u1E10\u1E12\u1E0E\u0110\u018B\u018A\u0189\uA779" },
    { base: "DZ", letters: "\u01F1\u01C4" },
    { base: "Dz", letters: "\u01F2\u01C5" },
    { base: "E", letters: "\u0045\u24BA\uFF25\u00C8\u00C9\u00CA\u1EC0\u1EBE\u1EC4\u1EC2\u1EBC\u0112\u1E14\u1E16\u0114\u0116\u00CB\u1EBA\u011A\u0204\u0206"
                        + "\u1EB8\u1EC6\u0228\u1E1C\u0118\u1E18\u1E1A\u0190\u018E" },
    { base: "F", letters: "\u0046\u24BB\uFF26\u1E1E\u0191\uA77B" },
    { base: "G", letters: "\u0047\u24BC\uFF27\u01F4\u011C\u1E20\u011E\u0120\u01E6\u0122\u01E4\u0193\uA7A0\uA77D\uA77E" },
    { base: "H", letters: "\u0048\u24BD\uFF28\u0124\u1E22\u1E26\u021E\u1E24\u1E28\u1E2A\u0126\u2C67\u2C75\uA78D" },
    { base: "I", letters: "\u0049\u24BE\uFF29\u00CC\u00CD\u00CE\u0128\u012A\u012C\u0130\u00CF\u1E2E\u1EC8\u01CF\u0208\u020A\u1ECA\u012E\u1E2C\u0197" },
    { base: "J", letters: "\u004A\u24BF\uFF2A\u0134\u0248" },
    { base: "K", letters: "\u004B\u24C0\uFF2B\u1E30\u01E8\u1E32\u0136\u1E34\u0198\u2C69\uA740\uA742\uA744\uA7A2" },
    { base: "L", letters: "\u004C\u24C1\uFF2C\u013F\u0139\u013D\u1E36\u1E38\u013B\u1E3C\u1E3A\u0141\u023D\u2C62\u2C60\uA748\uA746\uA780" },
    { base: "LJ", letters: "\u01C7" },
    { base: "Lj", letters: "\u01C8" },
    { base: "M", letters: "\u004D\u24C2\uFF2D\u1E3E\u1E40\u1E42\u2C6E\u019C" },
    { base: "N", letters: "\u004E\u24C3\uFF2E\u01F8\u0143\u00D1\u1E44\u0147\u1E46\u0145\u1E4A\u1E48\u0220\u019D\uA790\uA7A4" },
    { base: "NJ", letters: "\u01CA" },
    { base: "Nj", letters: "\u01CB" },
    { base: "O", letters: "\u004F\u24C4\uFF2F\u00D2\u00D3\u00D4\u1ED2\u1ED0\u1ED6\u1ED4\u00D5\u1E4C\u022C\u1E4E\u014C\u1E50\u1E52\u014E\u022E\u0230\u00D6"
                        + "\u022A\u1ECE\u0150\u01D1\u020C\u020E\u01A0\u1EDC\u1EDA\u1EE0\u1EDE\u1EE2\u1ECC\u1ED8\u01EA\u01EC\u00D8\u01FE\u0186\u019F\uA74A\uA74C" },
    { base: "OI", letters: "\u01A2" },
    { base: "OO", letters: "\uA74E" },
    { base: "OU", letters: "\u0222" },
    { base: "OE", letters: "\u008C\u0152" },
    { base: "oe", letters: "\u009C\u0153" },
    { base: "P", letters: "\u0050\u24C5\uFF30\u1E54\u1E56\u01A4\u2C63\uA750\uA752\uA754" },
    { base: "Q", letters: "\u0051\u24C6\uFF31\uA756\uA758\u024A" },
    { base: "R", letters: "\u0052\u24C7\uFF32\u0154\u1E58\u0158\u0210\u0212\u1E5A\u1E5C\u0156\u1E5E\u024C\u2C64\uA75A\uA7A6\uA782" },
    { base: "S", letters: "\u0053\u24C8\uFF33\u1E9E\u015A\u1E64\u015C\u1E60\u0160\u1E66\u1E62\u1E68\u0218\u015E\u2C7E\uA7A8\uA784" },
    { base: "T", letters: "\u0054\u24C9\uFF34\u1E6A\u0164\u1E6C\u021A\u0162\u1E70\u1E6E\u0166\u01AC\u01AE\u023E\uA786" },
    { base: "TZ", letters: "\uA728" },
    { base: "U", letters: "\u0055\u24CA\uFF35\u00D9\u00DA\u00DB\u0168\u1E78\u016A\u1E7A\u016C\u00DC\u01DB\u01D7\u01D5\u01D9\u1EE6\u016E\u0170\u01D3\u0214\u0216"
                        + "\u01AF\u1EEA\u1EE8\u1EEE\u1EEC\u1EF0\u1EE4\u1E72\u0172\u1E76\u1E74\u0244" },
    { base: "V", letters: "\u0056\u24CB\uFF36\u1E7C\u1E7E\u01B2\uA75E\u0245" },
    { base: "VY", letters: "\uA760" },
    { base: "W", letters: "\u0057\u24CC\uFF37\u1E80\u1E82\u0174\u1E86\u1E84\u1E88\u2C72" },
    { base: "X", letters: "\u0058\u24CD\uFF38\u1E8A\u1E8C" },
    { base: "Y", letters: "\u0059\u24CE\uFF39\u1EF2\u00DD\u0176\u1EF8\u0232\u1E8E\u0178\u1EF6\u1EF4\u01B3\u024E\u1EFE" },
    { base: "Z", letters: "\u005A\u24CF\uFF3A\u0179\u1E90\u017B\u017D\u1E92\u1E94\u01B5\u0224\u2C7F\u2C6B\uA762" },
    { base: "a", letters: "\u0061\u24D0\uFF41\u1E9A\u00E0\u00E1\u00E2\u1EA7\u1EA5\u1EAB\u1EA9\u00E3\u0101\u0103\u1EB1\u1EAF\u1EB5\u1EB3\u0227\u01E1\u00E4\u01DF"
                        + "\u1EA3\u00E5\u01FB\u01CE\u0201\u0203\u1EA1\u1EAD\u1EB7\u1E01\u0105\u2C65\u0250" },
    { base: "aa", letters: "\uA733" },
    { base: "ae", letters: "\u00E6\u01FD\u01E3" },
    { base: "ao", letters: "\uA735" },
    { base: "au", letters: "\uA737" },
    { base: "av", letters: "\uA739\uA73B" },
    { base: "ay", letters: "\uA73D" },
    { base: "b", letters: "\u0062\u24D1\uFF42\u1E03\u1E05\u1E07\u0180\u0183\u0253" },
    { base: "c", letters: "\u0063\u24D2\uFF43\u0107\u0109\u010B\u010D\u00E7\u1E09\u0188\u023C\uA73F\u2184" },
    { base: "d", letters: "\u0064\u24D3\uFF44\u1E0B\u010F\u1E0D\u1E11\u1E13\u1E0F\u0111\u018C\u0256\u0257\uA77A" },
    { base: "dz", letters: "\u01F3\u01C6" },
    { base: "e", letters: "\u0065\u24D4\uFF45\u00E8\u00E9\u00EA\u1EC1\u1EBF\u1EC5\u1EC3\u1EBD\u0113\u1E15\u1E17\u0115\u0117\u00EB\u1EBB\u011B\u0205\u0207\u1EB9"
                        + "\u1EC7\u0229\u1E1D\u0119\u1E19\u1E1B\u0247\u025B\u01DD" },
    { base: "f", letters: "\u0066\u24D5\uFF46\u1E1F\u0192\uA77C" },
    { base: "g", letters: "\u0067\u24D6\uFF47\u01F5\u011D\u1E21\u011F\u0121\u01E7\u0123\u01E5\u0260\uA7A1\u1D79\uA77F" },
    { base: "h", letters: "\u0068\u24D7\uFF48\u0125\u1E23\u1E27\u021F\u1E25\u1E29\u1E2B\u1E96\u0127\u2C68\u2C76\u0265" },
    { base: "hv", letters: "\u0195" },
    { base: "i", letters: "\u0069\u24D8\uFF49\u00EC\u00ED\u00EE\u0129\u012B\u012D\u00EF\u1E2F\u1EC9\u01D0\u0209\u020B\u1ECB\u012F\u1E2D\u0268\u0131" },
    { base: "j", letters: "\u006A\u24D9\uFF4A\u0135\u01F0\u0249" },
    { base: "k", letters: "\u006B\u24DA\uFF4B\u1E31\u01E9\u1E33\u0137\u1E35\u0199\u2C6A\uA741\uA743\uA745\uA7A3" },
    { base: "l", letters: "\u006C\u24DB\uFF4C\u0140\u013A\u013E\u1E37\u1E39\u013C\u1E3D\u1E3B\u017F\u0142\u019A\u026B\u2C61\uA749\uA781\uA747" },
    { base: "lj", letters: "\u01C9" },
    { base: "m", letters: "\u006D\u24DC\uFF4D\u1E3F\u1E41\u1E43\u0271\u026F" },
    { base: "n", letters: "\u006E\u24DD\uFF4E\u01F9\u0144\u00F1\u1E45\u0148\u1E47\u0146\u1E4B\u1E49\u019E\u0272\u0149\uA791\uA7A5" },
    { base: "nj", letters: "\u01CC" },
    { base: "o", letters: "\u006F\u24DE\uFF4F\u00F2\u00F3\u00F4\u1ED3\u1ED1\u1ED7\u1ED5\u00F5\u1E4D\u022D\u1E4F\u014D\u1E51\u1E53\u014F\u022F\u0231\u00F6\u022B"
                        + "\u1ECF\u0151\u01D2\u020D\u020F\u01A1\u1EDD\u1EDB\u1EE1\u1EDF\u1EE3\u1ECD\u1ED9\u01EB\u01ED\u00F8\u01FF\u0254\uA74B\uA74D\u0275" },
    { base: "oi", letters: "\u01A3" },
    { base: "ou", letters: "\u0223" },
    { base: "oo", letters: "\uA74F" },
    { base: "p", letters: "\u0070\u24DF\uFF50\u1E55\u1E57\u01A5\u1D7D\uA751\uA753\uA755" },
    { base: "q", letters: "\u0071\u24E0\uFF51\u024B\uA757\uA759" },
    { base: "r", letters: "\u0072\u24E1\uFF52\u0155\u1E59\u0159\u0211\u0213\u1E5B\u1E5D\u0157\u1E5F\u024D\u027D\uA75B\uA7A7\uA783" },
    { base: "s", letters: "\u0073\u24E2\uFF53\u00DF\u015B\u1E65\u015D\u1E61\u0161\u1E67\u1E63\u1E69\u0219\u015F\u023F\uA7A9\uA785\u1E9B" },
    { base: "t", letters: "\u0074\u24E3\uFF54\u1E6B\u1E97\u0165\u1E6D\u021B\u0163\u1E71\u1E6F\u0167\u01AD\u0288\u2C66\uA787" },
    { base: "tz", letters: "\uA729" },
    { base: "u", letters: "\u0075\u24E4\uFF55\u00F9\u00FA\u00FB\u0169\u1E79\u016B\u1E7B\u016D\u00FC\u01DC\u01D8\u01D6\u01DA\u1EE7\u016F\u0171\u01D4\u0215\u0217"
                        + "\u01B0\u1EEB\u1EE9\u1EEF\u1EED\u1EF1\u1EE5\u1E73\u0173\u1E77\u1E75\u0289" },
    { base: "v", letters: "\u0076\u24E5\uFF56\u1E7D\u1E7F\u028B\uA75F\u028C" },
    { base: "vy", letters: "\uA761" },
    { base: "w", letters: "\u0077\u24E6\uFF57\u1E81\u1E83\u0175\u1E87\u1E85\u1E98\u1E89\u2C73" },
    { base: "x", letters: "\u0078\u24E7\uFF58\u1E8B\u1E8D" },
    { base: "y", letters: "\u0079\u24E8\uFF59\u1EF3\u00FD\u0177\u1EF9\u0233\u1E8F\u00FF\u1EF7\u1E99\u1EF5\u01B4\u024F\u1EFF" },
    { base: "z", letters: "\u007A\u24E9\uFF5A\u017A\u1E91\u017C\u017E\u1E93\u1E95\u01B6\u0225\u0240\u2C6C\uA763" },
];

/**
 * Adds or removes an item from an array.
 * @param arr The array
 * @param value Value to add or remove
 * @param add Whether to add or remove
 */
export const addToOrRemoveFromArray = <T>(arr: T[], value: T, add: boolean) => {
    const idx = arr.indexOf(value);
    if (add && idx === -1)
        arr.push(value);
    else if (!add && idx > -1)
        arr = arr.splice(idx, 1);
};

/**
 * Checks the given object for the existence of a given key.
 * @param obj Object to check for keys
 * @param key The key to check
 * @param [ignoreCase] Whether or not to ignore case of the key. Defaults to true.
 */
export const containsKey = (obj: object | undefined | null, key: string, ignoreCase: boolean = true) => {
    if (obj === undefined || obj === null)
        return false;

    const keys = Object.keys(obj);

    for (const k in Object.keys(obj)) {
        if (obj.hasOwnProperty(keys[k])) {
            const compareTo = keys[k];
            if (compareTo === key || (ignoreCase && compareTo.toLowerCase() === key.toLowerCase()))
                return true;
        }
    }

    return false;
};

/**
 * Convertes a comma string (ie "3,5,2,99") into an array
 * @param str Value to be parsed into array
 * @param [splitBy] Split by character. Defaults to ","
 */
export const commaStringToArray = (str: string | undefined | null, splitBy: string = ",") => {
    const arr: string[] = [];
    if (str === undefined || str === null)
        return arr;
    return (str)
        .split(",")
        .filter((x) => x !== "");
};

/**
 * Convertes a comma string (ie "3,5,2,99") into a number array
 * @param str Value to be parsed into number array
 * @param [splitBy] Split by character. Defaults to ","
 */
export const commaStringToIntArray = (str: string | undefined | null, splitBy: string = ",") => {
    const ints: number[] = [];
    if (str === undefined || str === null)
        return ints;
    return (str)
        .split(",")
        .filter((x) => x !== "")
        .map((x) => parseInt(x, 10));
};

/**
 * Creates an index map from an object array where the index is a number
 * @param objArray The object array to be indexed
 * @param field The field that will serve as the index
 */
export const createNumberIndexMap = <T>(objArray: T[], field: (obj: T) => number): { [index: number]: T; } => {
    const map: {[index: number]: T} = {};
    objArray.map((t) => (map[field(t)] = t));
    return map;
};

/**
 * Creates an index map from an object array where the index is a string
 * @param objArray The object array to be indexed
 * @param field The field that will serve as the index
 */
export const createStringIndexMap = <T>(objArray: T[], field: (obj: T) => string): { [index: string]: T; } => {
    const map: {[index: string]: T} = {};
    objArray.map((t) => (map[field(t)] = t));
    return map;
};

/**
 * Returns a promise that will fail with the provided error message
 * @param error The error message of the failed promise
 * @returns {Promise<T>}
 */
export const failedPromise = <T>(error: string) => {
    return new Promise<T>((resolve, reject) => {
        try {
            throw error;
        }
        catch (innerError) {
            reject (innerError);
        }
    });
};

/**
 * Returns a formatted display of file size (ie "1,35 mb")
 * @param n The number to be formatted as a file size string.
 * @param decimals Number of decimals to display. Defaults to 0.
 * @param upperCase Display output in uppercase. Defaults to false.
 */
export const formatFileSize = (n: string | number | undefined | null, decimals: number = 0, upperCase: boolean = false): string => {
    if (n === undefined || n === null)
        return "";

    n = parseFloat(n.toString());
    decimals = decimals === undefined ? 0 : decimals;
    upperCase = upperCase === undefined ? false : upperCase;

    if (isNaN(n))
        return n.toString();

    let d = n + "kb";
    const kb = 1024;
    const mb = kb * 1024;
    const gb = mb * 1024;
    const tb = gb * 1024;

    if (n < kb)
        d = formatThousands(n.toFixed(decimals)) + " b";
    else if (n >= kb && n < mb) // 1kb < n < 1mb
        d = formatThousands((n / kb).toFixed(decimals)) + " kb";
    else if (n >= mb && n < gb) // 1mb < n < 1gb
        d = formatThousands((n / mb).toFixed(decimals)) + " mb";
    else if (n >= gb && n < tb) // 1bb < n < 1tb
        d = formatThousands((n / gb).toFixed(decimals)) + " gb";
    else
        d = formatThousands((n / tb).toFixed(decimals)) + " tb";

    return upperCase
        ? d.toUpperCase()
        : d;
};

export const formatThousands = (n: string | number | undefined | null, separator: string = ",", decChar: string = "."): string => {
    let f = "";

    if (n === undefined || n === null)
        return "";

    // some countries have different formatting
    // US formatting: x,xxx,xxx.yy
    // DE formatting: x.xxx.xxx,yy
    decChar = decChar === undefined ? "." : decChar;
    let dec = "";

    let s = n.toString();
    const sep = separator === undefined ? "," : separator;

    if (s.lastIndexOf(decChar) > -1) {
        dec = s.substring(s.lastIndexOf(decChar));
        s = s.substring(0, s.lastIndexOf(decChar));
    }

    s = s.split("").reverse().join("");

    if (n < 1000)
        f = s;
    else if (n >= 1000 && n < 1000000)
        f = s.substring(0, 3) + sep + s.substring(3);
    else if (n >= 1000000 && n < 1000000000)
        f = s.substring(0, 3) + sep + s.substring(3, 6) + sep + s.substring(6);
    else if (n >= 1000000000 && n < 1000000000000)
        f = s.substring(0, 3) + sep + s.substring(3, 6) + sep + s.substring(6, 9) + sep + s.substring(9);
    else
        f = s.substring(0, 3) + sep + s.substring(3, 6) + sep + s.substring(6, 9) + sep + s.substring(9, 12) + sep + s.substring(12);

    return f.split("").reverse().join("") + dec;
};

/**
 * Converts an API date string into a Date object.
 * @param dateStr The API date string
 */
export const fromApiDate = (dateStr: string | undefined | null): Date | undefined => {
    if (!dateStr)
        return undefined;
    return moment(dateStr, "YYYY-MM-DDTHH:mm:ss").toDate();
};

/**
 * Converts an API date string into a Date object.
 * @param dateStr The API date string
 */
export const fromApiDateAsMoment = (dateStr: string | undefined | null): moment.Moment => {
    if (!dateStr)
        return moment();
    return moment(dateStr, "YYYY-MM-DDTHH:mm:ss");
};

export const fromApiDateToValue = (dateStr: string | undefined | null): number => {
    const d = fromApiDate(dateStr);
    if (!d)
        return 0;
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds(), d.getMilliseconds());
};

export const getBrowser = () => {
    if (typeof navigator === "undefined")
        return undefined;

    const nAgt = navigator.userAgent;
    let browserName = navigator.appName;
    let fullVersion = "" + parseFloat(navigator.appVersion);
    let majorVersion = parseInt(navigator.appVersion, 10);
    let nameOffset, verOffset, ix;

    // In Opera, the true version is after "Opera" or after "Version"
    try {
        if ((verOffset = nAgt.indexOf("Opera")) !== -1) {
            browserName = "Opera";
            fullVersion = nAgt.substring(verOffset + 6);
            if ((verOffset = nAgt.indexOf("Version")) !== -1)
                fullVersion = nAgt.substring(verOffset + 8);
        }
        // In MSIE, the true version is after "MSIE" in userAgent
        else if ((verOffset = nAgt.indexOf("MSIE")) !== -1) {
            browserName = "Microsoft Internet Explorer";
            fullVersion = nAgt.substring(verOffset + 5);
        }
        // In Chrome, the true version is after "Chrome"
        else if ((verOffset = nAgt.indexOf("Chrome")) !== -1) {
            browserName = "Chrome";
            fullVersion = nAgt.substring(verOffset + 7);
        }
        // In Safari, the true version is after "Safari" or after "Version"
        else if ((verOffset = nAgt.indexOf("Safari")) !== -1) {
            browserName = "Safari";
            fullVersion = nAgt.substring(verOffset + 7);
            if ((verOffset = nAgt.indexOf("Version")) !== -1)
                fullVersion = nAgt.substring(verOffset + 8);
        }
        // In Firefox, the true version is after "Firefox"
        else if ((verOffset = nAgt.indexOf("Firefox")) !== -1) {
            browserName = "Firefox";
            fullVersion = nAgt.substring(verOffset + 8);
        }
        // In most other browsers, "name/version" is at the end of userAgent
        else if ((nameOffset = nAgt.lastIndexOf(" ") + 1) < (verOffset = nAgt.lastIndexOf("/"))) {
            browserName = nAgt.substring(nameOffset, verOffset);
            fullVersion = nAgt.substring(verOffset + 1);
            if (browserName.toLowerCase() === browserName.toUpperCase()) {
                browserName = navigator.appName;
            }
        }

        // trim the fullVersion string at semicolon/space if present
        if ((ix = fullVersion.indexOf(";")) !== -1)
            fullVersion = fullVersion.substring(0, ix);
        if ((ix = fullVersion.indexOf(" ")) !== -1)
            fullVersion = fullVersion.substring(0, ix);

        majorVersion = parseInt("" + fullVersion, 10);
        if (isNaN(majorVersion)) {
            fullVersion = "" + parseFloat(navigator.appVersion);
            majorVersion = parseInt(navigator.appVersion, 10);
        }
    }
    catch (err) { }

    const browser: M.Generic.Browser = {
        fullVersion,
        majorVersion,
        platform: navigator.platform,
        screenHeight: typeof screen !== "undefined" ? screen.height : undefined,
        screenWidth: typeof screen !== "undefined" ? screen.width : undefined,
        userAgent: navigator.userAgent,
    };

    return browser;
};

/**
 * Computes the position of an anchor element given an html element and the anchor position type.
 * @param el The element for which to compute the anchor position
 * @param positionType The anchor position type (ie "top", "bottom-right", "left", etc)
 */
export const getElementAnchorPosition = (el: HTMLElement, positionType: M.Generic.ElementAnchorPositionType): M.Generic.ElementPosition => {
    const anchor: M.Generic.ElementPosition = { top: 0, left: 0 };
    if (el === null || el === undefined)
        return anchor;

    const rect = el.getBoundingClientRect();
    const pos = getElementPosition(el);

    switch (positionType) {
        case "top-left":
        case "left-top":
            anchor.top = pos.top;
            anchor.left = pos.left;
            break;
        case "top":
            anchor.top = pos.top;
            anchor.left = pos.left + (rect.width / 2);
            break;
        case "top-right":
        case "right-top":
            anchor.top = pos.top;
            anchor.left = pos.left + rect.width;
            break;
        case "right":
            anchor.top = pos.top + (rect.height / 2);
            anchor.left = pos.left + rect.width;
            break;
        case "right-bottom":
        case "bottom-right":
            anchor.top = pos.top + rect.height;
            anchor.left = pos.left + rect.width;
            break;
        case "bottom":
            anchor.top = pos.top + rect.height;
            anchor.left = pos.left + (rect.width / 2);
            break;
        case "bottom-left":
        case "left-bottom":
            anchor.top = pos.top + rect.height;
            anchor.left = pos.left;
            break;
        case "left":
            anchor.top = pos.top + (rect.height / 2);
            anchor.left = pos.left;
            break;
    }

    return anchor;
};

/**
 * Returns a position object of an html object.
 * @param el The html element for which to get position for
 * @param [relativeToDoc] Whether or not to return position relative to document (with scrolls, etc). Defaults to true.
 */
export const getElementPosition = (el: HTMLElement | Element, relativeToDoc: boolean = true): M.Generic.ElementPosition => {
    const pos = el.getBoundingClientRect();

    if (!relativeToDoc)
        return { top: pos.top, left: pos.left };

    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: pos.top + scrollTop, left: pos.left + scrollLeft };
};

/**
 * Returns the full relative URL path including path, search, and hash parts
 */
export const getRelativePath = (includeSearch: boolean = true, includeHash: boolean = true): string => {
    if (typeof window !== "undefined" && window.location !== undefined)
        return window.location.pathname
            + (includeSearch ? window.location.search : "")
            + (includeHash ? window.location.hash : "");
    return "/";
};

/**
 * Returns the numerical ID from the slug (i.e. "some-title-with-id-55667")
 * @param slug The slug
 */
export const getIdFromSlug = (slug: string | undefined | null) => {
    if (!slug)
        return undefined;
    const dashIdx = slug.lastIndexOf("-");
    const id = parseInt(slug.substr(dashIdx + 1), 10);
    return isNaN(id) ? undefined : id;
};

/**
 * Gets the current window scroll position.
 * @returns {Point}
 */
export const getScrollPosition = (): M.Generic.Point => {
    const supportPageOffset = window.pageXOffset !== undefined;
    const isCSS1Compat = ((document.compatMode || "") === "CSS1Compat");
    const scroll: M.Generic.Point = {
        x: supportPageOffset
            ? window.pageXOffset
            : (isCSS1Compat ? document.documentElement.scrollLeft : document.body.scrollLeft),
        y: supportPageOffset
            ? window.pageYOffset
            : (isCSS1Compat ? document.documentElement.scrollTop : document.body.scrollTop),
    };

    return scroll;
};

/**
 * Returns the host of the provided URL string.
 * @param url The url. If url is undefined, assume to get location of current window.location.href.
 */
export const getUrlHost = (url?: string | undefined | null): string => {
    if (url === undefined)
        url = typeof window !== "undefined" ? window.location.href : undefined;

    if (!url || url.indexOf("//") === -1)
        return "";
    return url.split("/").slice(0, 3).join("/");
};

/**
 * Returns the base URL of the current window.location.href.
 * @param routePath The route path
 * @param includeHost Whether or not to include host.
 * @param [url] Complete url (optional). Will use window.location.href if not provided
 */
export const getUrlBase = (routePath: string, includeHost: boolean, url?: string): string => {
    url = !url
        ? (typeof window !== "undefined" ? window.location.href : undefined)
        : url;

    const host = getUrlHost(url);

    if (!url)
        return "";

    const baseUrl = url
        .replace(routePath, "")
        .replace(host, "");

    return includeHost
        ? host + baseUrl
        : baseUrl;
};

/**
 * Returns a GUID-like string
 */
export const guid = () => {
    const s4 = () => ((((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1));
    return (s4() + s4() + "-" + s4() + "-4" + s4().substr(0, 3) + "-" + s4() + "-" + s4() + s4() + s4()).toLowerCase();
};

/**
 * * Returns true if the provided URL does not start with a "/" or "#"
 * @param url The URL to check
 */
export const isExternalUrl = (url?: string): boolean => {
    if (!url)
        return false;
    return url.indexOf("/") !== 0 && url.indexOf("#") !== 0;
};

export const isGuid = (str?: string | null): boolean => {
    if (str === null || str === undefined)
        return false;
    return ((str + "").match(/([a-zA-Z0-9]{8})\-([a-zA-Z0-9]{4})\-([a-zA-Z0-9]{4})\-([a-zA-Z0-9]{4})\-([a-zA-Z0-9]{12})$/g) || []).length === 1;
};

export const isMergebleObject = (item: any): boolean => {
    return isObject(item) && !Array.isArray(item);
};

export const isNullOrUndefined = (obj: any): boolean => {
    return obj === undefined || obj === null;
};

export const isNullOrEmptyString = (str?: string | null): boolean => {
    if (str === undefined || str === null)
        return true;
    const trimmed = str.trim();
    return trimmed.length === 0;
};

export const isObject = (item: any): boolean => {
    return item !== null && typeof item === "object";
};

export const isValidEmail = (addr: string | undefined | null) => {
    if (!addr)
        return false;
    return /(.+)@(.+){2,}\.(.+){2,}/.test(addr);
};

/**
 * Returns a joined array string representation skipping empty, undefined, and null values.
 * @param arr The string array
 * @param delim The delim character
 */
export const joinSkipEmpty = (arr: Array<string | undefined | null>, delim: string): string => {
    if (!Array.isArray(arr) || !arr.length)
        return "";

    return arr
        .filter((a) => a !== undefined && a !== null && a.length)
        .join(delim);
};

/**
 * Lazy loads a resource from a URL
 * @param url The resource URL to load
 */
export const lazyLoadResource = (url: string, async?: boolean): Promise<{script: string, loaded: boolean, status: string}> => {
    // based on https://friendlybit.com/js/lazy-loading-asyncronous-javascript/
    // and https://stackoverflow.com/a/42766146/1235875
    return new Promise((resolve, reject) => {
        const scripts = document.getElementsByTagName("script");
        for (let i = scripts.length; i--;) {
            if (scripts[i].src.match(url))
                resolve({script: url, loaded: true, status: "Loaded"});
        }

        const script = document.createElement("script");
        script.type = "text/javascript";
        script.async = async === undefined ? true : async;
        script.src = url;

        if ((script as any).readyState) { // IE
            (script as any).onreadystatechange = () => {
                if ((script as any).readyState === "loaded" || (script as any).readyState === "complete") {
                    (script as any).onreadystatechange = null;
                    resolve({script: url, loaded: true, status: "Loaded"});
                }
            };
        } else { // Others
            script.onload = () => {
                resolve({script: url, loaded: true, status: "Loaded"});
            };
        }

        script.onerror = (error: any) => reject(error);

        const x = document.getElementsByTagName("script")[0];
        if (x.parentNode)
            x.parentNode.insertBefore(script, x);
    });
};

/**
 * Deep-merges objects
 * https://stackoverflow.com/questions/27936772/how-to-deep-merge-instead-of-shallow-merge
 */
export const mergeObjects = <T extends object = object>(target: T, ...sources: T[]): T  => {
    if (!sources.length)
        return target;

    const source = sources.shift();
    if (source === undefined)
        return target;

    if (isMergebleObject(target) && isMergebleObject(source)) {
        Object.keys(source).forEach((key: string) => {
            if (isMergebleObject(source[key])) {
                if (!target[key])
                    target[key] = {};

                mergeObjects(target[key], source[key]);
            }
            else
            {
                target[key] = source[key];
            }
        });
    }

    return mergeObjects(target, ...sources);
};

/**
 * Replaces new line characters with another character(s). Defaults to "<br />"
 * @param str The string to modify
 */
export const nl2br = (str: string | null | undefined, replaceWith: string = "<br />") => {
    if (str)
        return str.replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, "$1" + replaceWith + "$2");
    return str;
};

/**
 * Converts a number to word if number is within range [-12, 12].
 * @param num The number
 * @param toLowerCase To lower case?
 */
export const numberToWord = (num?: number | null, toLowerCase: boolean = false) => {
    if (num === undefined || num === null || typeof num !== "number")
        return "";
    const numberWords = ["Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve"];
    if (num > 12 || num < -12)
        return num.toString();

    const word = num < 0
        ? `Negative ${numberWords[-num]}`
        : numberWords[num];

    return toLowerCase ? word.toLowerCase() : word;
};

/**
 * Converts a flat json parameters object into a URL friendly string.
 * @param params The object parameters
 */
export const queryParamsToUrl = (params: object) => {
    return Object.keys(params)
        .filter((k) => (params as any)[k] !== null && (params as any)[k] !== undefined)
        .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent((params as any)[k])}`)
        .join("&")
        .replace(/%2C/g, ",");
};

/**
 * Pads the given string with the given character
 * @param str The string to be padded
 * @param char The character to pad the string with
 * @param spaces Number of total spaces when padded
 * @param direction Pad left ("l") or right ("r"). Defaults to left ("l")
 */
export const padText = (str: string | undefined | null, char: string, spaces: number, direction: "l" | "r" = "l") => {
    str = str || "";
    if (str.length >= spaces)
        return str;

    if (direction === "r")
        str = str.split("").reverse().join("");

    let newText = "";
    const strZeroIdx = spaces - str.length;

    for (let i = 0; i < spaces; i++) {
        if (i >= strZeroIdx) {
            newText += str[i - strZeroIdx];
        }
        else {
            newText += char;
        }
    }

    if (direction === "r")
        newText = newText.split("").reverse().join("");

    return newText;
};

export const removeDiacritics = (str: string | undefined | null) => {
    if (str === undefined || str === null)
        return str;

    const diacriticsMap = {};
    for (const map of diacriticsRemovalMap) {
        const letters = map.letters;
        for (const l of letters) {
            diacriticsMap[l] = map.base;
        }
    }

    // for (let i = 0; i < Utility.diacriticsRemovalMap.length; i++) {
    //     const letters = Utility.diacriticsRemovalMap[i].letters;
    //     for (var j = 0; j < letters.length ; j++) {
    //         diacriticsMap[letters[j]] = Utility.diacriticsRemovalMap[i].base;
    //     }
    // }

    return str.replace(/[^\u0000-\u007E]/g, (a: string) => {
        return diacriticsMap[a] || a;
    });
};

/**
 * Sorts a filtered array by relevance on a specific field or field delegate
 * @param arr The array to sort
 * @param field The field (or field delegate) on which to add relevance
 * @param search The search text
 * @param [secondaryOrderBy] Optionally on which field(s) to sort by after relevance
 */
export const sortFilteredArrayByRelevance = <T>(arr: T[], field: (obj: T) => string | undefined, search: string, secondaryOrderBy?: string[]) => {
    search = search.toLowerCase();
    return _.orderBy(arr.map((x) => {
        const fieldVal = (field(x) || "").toLowerCase();
        let score = 3;
        if (fieldVal === search)
            score = 1;
        else if (fieldVal.indexOf(search) === 0)
            score = 2;

        // add score field
        (x as any).__score = score;

        return x;
    }), ["__score", ...(secondaryOrderBy || [])]);
};

/**
 * Strips html tags from string variable
 * @param html The string
 */
export const stripHtml = (html: string | undefined | null) => {
    if (!html)
        return html;
    return html.replace(/<[^>]+>/g, "");
};

/**
 * Converts a string to an HTML appropriate id string.
 * @param str The string to convert to an HTML id string
 */
export const toHtmlIdString = (str?: string) => {
    if (!str)
        return str;
    return str.replace(/((?!([a-zA-Z\d\_\-])).)/g, "_");
};

/**
 * Converts an object into a react-select option item
 * @param obj The object to convert
 */
export const toReactSelectOption = <T extends object>(obj: T, label: string, value: string | number | boolean): T & M.Generic.ReactSelectOption => {
    return {
        label,
        value,
        ...obj as any,
    };
};

/**
 * Converts a string to sentence case ()
 * @param value The string value to convert
 * @param locale
 * @param mergeNumbers
 */
export const toSentenceCase = (value: string | undefined | null, locale?: string, mergeNumbers?: boolean): string => {
    if (!value)
        return "";
    return sentenceCase(value, locale, mergeNumbers);
};

/**
 * Inspired by https://gist.github.com/mathewbyrne/1280286
 * @param text The text to convert to slug
 * @param [toLowerCase] Whether or not to convert the result to lower case. Defaults to true.
 */
export const toSlug = (text: string | undefined | null, toLowerCase: boolean = true) => {
    text = removeDiacritics(text);
    if (text === undefined || text === null)
        return text;

    const result = text.toString()
        .replace(/\s+/g, "-")           // Replace spaces with -
        .replace(/[^\w\-]+/g, "")       // Remove all non-word chars
        .replace(/\-\-+/g, "-")         // Replace multiple - with single -
        .replace(/^-+/, "")             // Trim - from start of text
        .replace(/-+$/, "");            // Trim - from end of text

    return toLowerCase ? result.toLowerCase() : result;
};

export const trim = (str: string | undefined | null) => {
    if (!str)
        return str;
    const rtrim = /^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g;
    return str.replace(rtrim, "");
};

export const utcToLocal = (utcDate: Date | string, format?: string): Date => {
    if (!format)
        format = "YYYY-MM-DDTHH:mm:ss";

    if (typeof utcDate === "string") {
        const _utcDate = moment.utc(utcDate, format).toDate();
        return moment(_utcDate).local().toDate();
    }
    else {
        const _utcDate = moment.utc(utcDate).toDate();
        return moment(_utcDate).local().toDate();
    }
};

export const utcToLocalAsMoment = (utcDate: Date | string, format?: string): moment.Moment => {
    if (!format)
        format = "YYYY-MM-DDTHH:mm:ss";

    return typeof utcDate === "string"
        ? moment.utc(utcDate, format).local()
        : moment.utc(utcDate).local();
};

export const utcToValue = (utcDate: Date | string, format?: string): number => {
    const d = utcToLocal(utcDate, format);
    return Utility.toUtcValue(d);
};

export const toUtcValue = (d: Date) => {
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds(), d.getMilliseconds());
};

/**
 * Updates the value of an object"s property using string notation.
 * Example "person.phones[0].number"
 * @param obj The object to be updated
 * @param name The full name of the property to be updated
 * @param value The value to update the object"s property with
 */
export const updateObjectByKey = (obj: any, name: string, value: any) => {
    const dotIdx = name.indexOf(".");
    let key = dotIdx > 0
        ? name.substr(0, dotIdx)
        : name;

    const keySuffix = dotIdx > 0
        ? name.substr(dotIdx + 1)
        : "";

    try {
        // update the object"s field if there is no dot
        const doUpdate = dotIdx === -1;
        let isArray = false;
        let arrayIdx = -1;

        const regex = new RegExp(/(\[\d+\])/g);
        const matches = regex.exec(key);

        if (matches && matches.length) {
            // key is array indexer
            arrayIdx = parseInt(matches[0].replace(/[\[\]]/g, ""), 10);
            key = key.replace(regex, "");
            isArray = arrayIdx > -1;
        }

        if (doUpdate) {
            if (isArray)
                (obj[key])[arrayIdx] = value;
            else
                obj[key] = value;
        }
        else {
            if (isArray) {
                updateObjectByKey(obj[key][arrayIdx], keySuffix, value);
            }
            else
                updateObjectByKey(obj[key], keySuffix, value);
        }
    }
    catch (err) {
        throw new Error(`Unable to update object"s property (object.${key}) with value ${value}. ` + err);
    }
};



// WEBPACK FOOTER //
// ./src/utils/Utility.ts